import { useState, useEffect, useMemo } from 'react';
import { api } from '@/lib/api-client';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Check, X, Layers, Plus } from 'lucide-react';
import { getCapabilityColors } from '@/lib/capability-colors';
import { cn } from '@/lib/utils';
import {
    CapabilityPickerDrawer,
    type CapabilityPickerItem,
} from '@/components/agent-config/CapabilityPickerDrawer';
import { ParameterMappingPanel } from './parameter-mapping-panel';
import {
    createDefaultMethod,
    extractPathParams,
    type MethodFormData,
    type HttpVerb,
} from '@/types/integration-form-types';

const VERBS: HttpVerb[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

const VERB_COLORS: Record<string, string> = {
    GET: 'bg-blue-100 text-blue-700',
    POST: 'bg-green-100 text-green-700',
    PUT: 'bg-amber-100 text-amber-700',
    PATCH: 'bg-orange-100 text-orange-700',
    DELETE: 'bg-red-100 text-red-700',
};

interface MethodEditorDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    method?: MethodFormData;
    existingNames?: string[];
    onSave: (method: MethodFormData) => void;
    appKey?: string;
    currentUserId?: string;
    currentOrgId?: string;
}

interface SelectedSchema {
    ref: string;
    name: string;
    description?: string;
}

export function MethodEditorDrawer({
    open,
    onOpenChange,
    method,
    existingNames = [],
    onSave,
    appKey = '',
    currentUserId = '',
    currentOrgId = '',
}: MethodEditorDrawerProps) {
    const [form, setForm] = useState<MethodFormData>(createDefaultMethod());
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [schemaPickerOpen, setSchemaPickerOpen] = useState(false);
    const [schemaTarget, setSchemaTarget] = useState<'input' | 'output' | null>(null);
    const [schemaItems, setSchemaItems] = useState<CapabilityPickerItem[]>([]);
    const [schemaLoading, setSchemaLoading] = useState(false);
    const [selectedInputSchema, setSelectedInputSchema] = useState<SelectedSchema | null>(null);
    const [selectedOutputSchema, setSelectedOutputSchema] = useState<SelectedSchema | null>(null);

    const isEditing = !!method;
    const colors = getCapabilityColors('integration');
    const TypeIcon = colors.icon;

    useEffect(() => {
        if (open) {
            setForm(method ? { ...method } : createDefaultMethod());
            setErrors({});

            const resolveSchemaRef = (raw: string | undefined): SelectedSchema | null => {
                if (!raw) return null;
                try {
                    const parsed = JSON.parse(raw);
                    if (parsed && typeof parsed.$ref === 'string') {
                        return { ref: parsed.$ref, name: parsed.$ref };
                    }
                } catch {
                    const trimmed = raw.trim();
                    if (trimmed && !trimmed.startsWith('{') && trimmed.includes('/')) {
                        return { ref: trimmed, name: trimmed };
                    }
                }
                return null;
            };

            setSelectedInputSchema(resolveSchemaRef(method?.inputSchema));
            setSelectedOutputSchema(resolveSchemaRef(method?.outputSchema));
        }
    }, [open, method]);

    useEffect(() => {
        if (!open) return;
        setSchemaLoading(true);
        api.get('/capabilities', { params: { type: 'schema', per_page: 500, include_entitled: 'true' } })
            .then((res) => {
                const items = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
                setSchemaItems(
                    items.map((item: Record<string, unknown>) => ({
                        name: item.name as string,
                        description: (item.description as string) ?? undefined,
                        tags: (item.tags as string[]) ?? [],
                        fqcn: item.app_key
                            ? `${item.app_key}/${item.name}`
                            : (item.name as string),
                        app_key: item.app_key as string,
                        visibility: (item.visibility as string) ?? 'account',
                        created_by: item.created_by as string,
                        organization_id: item.organization_id as string,
                        status: (item.status as string) ?? 'active',
                        type: (item.type as string) ?? 'schema',
                    })),
                );
            })
            .catch(() => setSchemaItems([]))
            .finally(() => setSchemaLoading(false));
    }, [open]);

    useEffect(() => {
        if (schemaItems.length === 0) return;
        if (selectedInputSchema) {
            const found = schemaItems.find((s) => (s.fqcn ?? s.name) === selectedInputSchema.ref);
            if (found && selectedInputSchema.name === selectedInputSchema.ref) {
                setSelectedInputSchema({ ref: selectedInputSchema.ref, name: found.name, description: found.description ?? undefined });
            }
        }
        if (selectedOutputSchema) {
            const found = schemaItems.find((s) => (s.fqcn ?? s.name) === selectedOutputSchema.ref);
            if (found && selectedOutputSchema.name === selectedOutputSchema.ref) {
                setSelectedOutputSchema({ ref: selectedOutputSchema.ref, name: found.name, description: found.description ?? undefined });
            }
        }
    }, [schemaItems, selectedInputSchema, selectedOutputSchema]);

    const update = (patch: Partial<MethodFormData>) => {
        const updated = { ...form, ...patch };
        if ('path' in patch) {
            updated.pathParams = extractPathParams(patch.path!);
        }
        setForm(updated);
    };

    const detectedPathParams = useMemo(() => extractPathParams(form.path), [form.path]);
    const hasBody = form.verb === 'POST' || form.verb === 'PUT' || form.verb === 'PATCH';

    const validate = (): boolean => {
        const errs: Record<string, string> = {};
        if (!form.name.trim()) errs.name = 'Method name is required';
        else if (!/^[a-z][a-z0-9_]*$/.test(form.name.trim()))
            errs.name = 'Use snake_case: lowercase letters, numbers, underscores';
        else if (!isEditing && existingNames.includes(form.name.trim()))
            errs.name = 'A method with this name already exists';
        if (!form.path.trim()) errs.path = 'Endpoint path is required';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;
        const saved: MethodFormData = {
            ...form,
            name: form.name.trim(),
            pathParams: detectedPathParams,
        };
        onSave(saved);
        onOpenChange(false);
    };

    const openSchemaPicker = (target: 'input' | 'output') => {
        setSchemaTarget(target);
        setSchemaPickerOpen(true);
    };

    const clearSchema = (target: 'input' | 'output') => {
        if (target === 'input') {
            setSelectedInputSchema(null);
            update({ inputSchema: '{\n  "type": "object",\n  "properties": {}\n}' });
        } else {
            setSelectedOutputSchema(null);
            update({ outputSchema: '{\n  "type": "object"\n}' });
        }
    };

    const handleSchemaSelect = (schemaRef: string) => {
        const found = schemaItems.find((s) => (s.fqcn ?? s.name) === schemaRef);
        const selected: SelectedSchema = {
            ref: schemaRef,
            name: found?.name ?? schemaRef,
            description: found?.description ?? undefined,
        };
        const schemaValue = JSON.stringify({ $ref: schemaRef }, null, 2);
        if (schemaTarget === 'input') {
            setSelectedInputSchema(selected);
            update({ inputSchema: schemaValue });
        } else if (schemaTarget === 'output') {
            setSelectedOutputSchema(selected);
            update({ outputSchema: schemaValue });
        }
        setSchemaPickerOpen(false);
        setSchemaTarget(null);
    };

    return (
        <>
            <SlideDrawer
                isOpen={open}
                onClose={() => onOpenChange(false)}
                zTier="stacked"
            >
                        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                            {/* Header — aligned with IntegrationCreateWizard shell */}
                            <div className="relative shrink-0 border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                                <div className="mb-1 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={cn(
                                                'flex h-10 w-10 items-center justify-center rounded-full',
                                                colors.bgLight,
                                            )}
                                        >
                                            <TypeIcon className={cn('h-5 w-5', colors.text)} />
                                        </div>
                                        <div>
                                            <h2 className="text-xl font-semibold text-slate-900">
                                                {isEditing ? 'Edit Method' : 'Add Method'}
                                            </h2>
                                            <p className="text-sm text-slate-500">
                                                {isEditing
                                                    ? 'Update endpoint mapping, parameters, and schemas.'
                                                    : 'Define HTTP verb, path, parameters, and JSON schemas.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onOpenChange(false)}
                                        className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                                        aria-label="Close"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4 [scrollbar-gutter:stable]">
                            <div className="space-y-5">
                                {/* Row 1: Name + Description */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label>Method Name <span className="text-red-500">*</span></Label>
                                        <Input
                                            placeholder="e.g. list_repos"
                                            value={form.name}
                                            onChange={(e) => update({ name: e.target.value })}
                                            className="font-mono text-sm"
                                            disabled={isEditing}
                                            autoFocus
                                        />
                                        {errors.name && (
                                            <p className="text-xs text-red-500 flex items-center gap-1">
                                                <AlertCircle size={12} /> {errors.name}
                                            </p>
                                        )}
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Description</Label>
                                        <Input
                                            placeholder="Brief description of what this method does..."
                                            value={form.description}
                                            onChange={(e) => update({ description: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Verb + Path + Semantics */}
                                <div className="flex gap-3">
                                    <div className="w-28 space-y-1.5">
                                        <Label>Verb <span className="text-red-500">*</span></Label>
                                        <Select value={form.verb} onValueChange={(v) => update({ verb: v as HttpVerb })}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {VERBS.map((v) => (
                                                    <SelectItem key={v} value={v}>
                                                        <Badge variant="secondary" className={`text-xs ${VERB_COLORS[v]}`}>{v}</Badge>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="flex-1 space-y-1.5">
                                        <Label>Endpoint Path <span className="text-red-500">*</span></Label>
                                        <Input
                                            placeholder="/repos/{{owner}}/{{repo}}"
                                            value={form.path}
                                            onChange={(e) => update({ path: e.target.value })}
                                            className="font-mono text-sm"
                                        />
                                        {errors.path && (
                                            <p className="text-xs text-red-500 flex items-center gap-1">
                                                <AlertCircle size={12} /> {errors.path}
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-44 space-y-1.5">
                                        <Label>Semantics</Label>
                                        <Select
                                            value={form.semantics}
                                            onValueChange={(v) => update({ semantics: v as 'query' | 'work' })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="query">Query (read-only)</SelectItem>
                                                <SelectItem value="work">Work (side effects)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Path Params (auto-detected) */}
                                {detectedPathParams.length > 0 && (
                                    <div className="space-y-1.5">
                                        <Label className="text-xs text-slate-500">Detected Path Parameters</Label>
                                        <div className="flex flex-wrap gap-1.5">
                                            {detectedPathParams.map((p) => (
                                                <Badge key={p} variant="secondary" className="text-xs font-mono">
                                                    {`{{${p}}}`}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Input Schema + Output Schema slots */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Input Schema Slot</Label>
                                        {selectedInputSchema ? (
                                            <Card
                                                role="button"
                                                tabIndex={0}
                                                aria-label="Browse or change input schema"
                                                className="relative cursor-pointer border border-slate-200 p-3 transition-colors hover:border-slate-300 hover:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                                onClick={() => openSchemaPicker('input')}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openSchemaPicker('input');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
                                                        <Layers size={16} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-semibold text-slate-900">
                                                            {selectedInputSchema.name}
                                                        </p>
                                                        <p className="truncate font-mono text-[10px] text-slate-400">
                                                            {selectedInputSchema.ref}
                                                        </p>
                                                        {selectedInputSchema.description && (
                                                            <p className="line-clamp-2 text-[10px] text-slate-500">
                                                                {selectedInputSchema.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-slate-400 hover:text-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            clearSchema('input');
                                                        }}
                                                        aria-label="Clear input schema"
                                                    >
                                                        <X size={12} />
                                                    </Button>
                                                </div>
                                            </Card>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => openSchemaPicker('input')}
                                                className="w-full rounded-md border-2 border-dashed border-slate-200 bg-slate-50/50 p-3 text-left hover:border-slate-300"
                                            >
                                                <div className="flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-slate-500">
                                                    <Plus size={14} />
                                                    Select input schema
                                                </div>
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Output Schema Slot</Label>
                                        {selectedOutputSchema ? (
                                            <Card
                                                role="button"
                                                tabIndex={0}
                                                aria-label="Browse or change output schema"
                                                className="relative cursor-pointer border border-slate-200 p-3 transition-colors hover:border-slate-300 hover:bg-slate-50/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                                onClick={() => openSchemaPicker('output')}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        openSchemaPicker('output');
                                                    }
                                                }}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-indigo-100 text-indigo-700">
                                                        <Layers size={16} />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <p className="truncate text-sm font-semibold text-slate-900">
                                                            {selectedOutputSchema.name}
                                                        </p>
                                                        <p className="truncate font-mono text-[10px] text-slate-400">
                                                            {selectedOutputSchema.ref}
                                                        </p>
                                                        {selectedOutputSchema.description && (
                                                            <p className="line-clamp-2 text-[10px] text-slate-500">
                                                                {selectedOutputSchema.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-slate-400 hover:text-red-500"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            clearSchema('output');
                                                        }}
                                                        aria-label="Clear output schema"
                                                    >
                                                        <X size={12} />
                                                    </Button>
                                                </div>
                                            </Card>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => openSchemaPicker('output')}
                                                className="w-full rounded-md border-2 border-dashed border-slate-200 bg-slate-50/50 p-3 text-left hover:border-slate-300"
                                            >
                                                <div className="flex items-center justify-center gap-1.5 py-3 text-xs font-medium text-slate-500">
                                                    <Plus size={14} />
                                                    Select output schema
                                                </div>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Query Params + Body Params side-by-side */}
                                <div className={`grid gap-4 ${hasBody ? 'grid-cols-2' : 'grid-cols-1'}`}>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-slate-700">
                                                Query Parameters
                                            </span>
                                            {form.queryParams.length > 0 && (
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {form.queryParams.length}
                                                </Badge>
                                            )}
                                        </div>
                                        <ParameterMappingPanel
                                            value={form.queryParams}
                                            onChange={(queryParams) => update({ queryParams })}
                                            keyPlaceholder="Query param key"
                                        />
                                    </div>

                                    {hasBody && (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-slate-700">
                                                    Body Parameters
                                                </span>
                                                {form.bodyParams.length > 0 && (
                                                    <Badge variant="secondary" className="text-[10px]">
                                                        {form.bodyParams.length}
                                                    </Badge>
                                                )}
                                            </div>
                                            <ParameterMappingPanel
                                                value={form.bodyParams}
                                                onChange={(bodyParams) => update({ bodyParams })}
                                                keyPlaceholder="Body param key"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            </div>

                            {/* Footer — aligned with IntegrationCreateWizard */}
                            <div className="flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                                <button
                                    type="button"
                                    onClick={() => onOpenChange(false)}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isEditing ? 'Save Changes' : 'Add Method'}
                                    <Check size={16} />
                                </button>
                            </div>
                        </div>
            </SlideDrawer>
            <CapabilityPickerDrawer
                isOpen={schemaPickerOpen}
                onClose={() => {
                    setSchemaPickerOpen(false);
                    setSchemaTarget(null);
                }}
                slotType="schema"
                items={schemaItems}
                selectedRefs={
                    schemaTarget === 'input'
                        ? selectedInputSchema
                            ? [selectedInputSchema.ref]
                            : []
                        : schemaTarget === 'output'
                          ? selectedOutputSchema
                              ? [selectedOutputSchema.ref]
                              : []
                          : []
                }
                onSelect={handleSchemaSelect}
                loading={schemaLoading}
                appKey={appKey}
                currentUserId={currentUserId}
                currentOrgId={currentOrgId}
                overlayZClassName="z-400"
                drawerZClassName="z-401"
            />
        </>
    );
}
